import React, { useState, useMemo, useCallback, useRef } from "react";

import {
  DoubleArrow,
  TableControls,
  EmptyTableRow
} from "../../../../../components/SharedComponents/sharedComponents.index";

import { i18n } from "../../../../../translate/i18n";

import PropTypes from "prop-types";

import { Select, SelectItem, Card, Text, Switch } from "@aidron/aidron-ds";

import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';

import { CustomText } from "../../../../../components/StyledComponents/styleComponents.index";

import {   
  COLORS, 
  COLUMNS_IDENTIFIERS, 
  HIDEABLE_COLUMNS  } from "../../../../../components/components.constants";
import { UpdatingModal } from "../../../../../components/SharedComponents/UpdatingModal/UpdatingModal";

const { t } = i18n;

// métodos auxiliares para ocultação de colunas
const getVisibleColumnIds = ({ table }) => {
  const visibility = table.getState().columnVisibility || {};
  return HIDEABLE_COLUMNS
    .filter(col => visibility[col.id] !== false)
    .map(col => col.id);
}

const handleColumnChange = ({ table, selectedIds }) => {
  const newVisibility = {};
  HIDEABLE_COLUMNS.forEach(col => {
    newVisibility[col.id] = selectedIds.includes(col.id);
  });
  table.setColumnVisibility(newVisibility);
}

const ColumnVisibilityMemo = React.memo(function ColumnVisibilityToggles({ actions, ctx, uiState  }) {

  const { t } = i18n;

  const { setFreezeColumns, setShowColumnFilters } = actions || {};
  const { table } = ctx || {};
  const { freezeColumns } = uiState || {};

  const visibleColumnIds = useMemo(() => getVisibleColumnIds({ table: table }), [table]);

  return (
    <Card style={{ backgroundColor: "rgb(252, 252, 252)" }}>

      <section>
        <div className="margin-on-switch-outer">
          <Switch handleChange={option => setFreezeColumns(option)} value={freezeColumns}>
            {t("freezeColumns")}
          </Switch>
        </div>

        <Text size="sm" weight="bold">
          {t("choseColumns")}
        </Text>

        <div className="mb-2 mx-2">
          <Select
            readOnly
            multiselect
            label=""
            handleChange={(selectedIds) => handleColumnChange({ table: table, selectedIds: selectedIds })}
            selected={visibleColumnIds}
          >
            {HIDEABLE_COLUMNS.map(col => (
              <SelectItem key={col.id} value={col.id}>
                {col.label}
              </SelectItem>
            ))}
          </Select>
        </div>

        <DoubleArrow 
          width={40}
          left={"40%"}
          height={40}
          onClick={() => setShowColumnFilters(prev => !prev)} />
      </section>
    </Card>
  );
})

// métodos auxiliares para tabela de recomendação de compras
const stickyColumnsStyles = ({ columnId, table }) => {
  const stickyColumnsOrder = [
    COLUMNS_IDENTIFIERS.SUPPLIERS,
    COLUMNS_IDENTIFIERS.PRODUCT_SUBGROUP_NAME,
    COLUMNS_IDENTIFIERS.PRODUCT_NAME,
  ];

  if (!stickyColumnsOrder.includes(columnId)) return undefined;

  let left = 0;
  for (const colId of stickyColumnsOrder) {
    if (colId === columnId) break;
    
    const element = document.querySelector(`th[data-column="${colId}"], td[data-column="${colId}"]`);
    if (element) {
      left += element.getBoundingClientRect().width;
    }
  }

  return left;
};

const bordersAndBackgroundColorStyles = ({ columnId }) => {
  const orangeBg = "#FEF1E9";

  const styles = {
    averageSales: {
      borderLeft: `1px solid ${COLORS.GRAY_VALUE}`,
      borderRight: `1px solid ${COLORS.GRAY_VALUE}`,
    },
    effectivePurchase: {
      backgroundColor: orangeBg,
      borderLeft: `1px solid ${COLORS.GRAY_VALUE}`,
      borderRight: `1px solid ${COLORS.GRAY_VALUE}`,
      paddingInline: "0.4rem"
    },
    leadTime: {
      borderLeft: `1px solid ${COLORS.GRAY_VALUE}`,
      borderRight: `1px solid ${COLORS.GRAY_VALUE}`,
      paddingInline: "0.4rem"
    },
    physicalStock: {
      borderLeft: `1px solid ${COLORS.GRAY_VALUE}`,
      paddingInline: "0.4rem"
    },
    minStock: {
      paddingInline: "0.4rem"
    },      
    data_for_purchase: {
      borderLeft: `1px solid ${COLORS.GRAY_VALUE}`,
      borderRight: `1px solid ${COLORS.GRAY_VALUE}`,
    },
    purchaseRec7: {
      backgroundColor: orangeBg,
      borderRight: `1px solid ${COLORS.GRAY_VALUE}`,
      paddingInline: "0.4rem"
    },
    purchaseRec14: {
      backgroundColor: orangeBg,
      borderRight: `1px solid ${COLORS.GRAY_VALUE}`,
    },
    purchaseRec21: {
      backgroundColor: orangeBg,
      borderRight: `1px solid ${COLORS.GRAY_VALUE}`,
    },
    purchase_proc: {
      backgroundColor: orangeBg,
      borderRight: `1px solid ${COLORS.GRAY_VALUE}`,
    },
    prefixName: {
      backgroundColor: orangeBg,
      paddingInline: "0.4rem"
    },
    packToUnitRatio: {
      backgroundColor: orangeBg,
      paddingInline: "0.4rem"
    },
    data_description: {
      backgroundColor: '#fff',
      position: 'sticky',
      left: 0,
      zIndex: 40,
    },
  };
  return styles[columnId] || {};
};

const flexColumnsStyles = ({ columnId }) => {
  const styles = {
    suppliers: { justifyContent: 'flex-start' },
    productSubgroupName: { justifyContent: 'flex-start' },
  };
  return styles[columnId] || { justifyContent: 'center' };
};

const getColumnStyles = ({ columnId }) => {
  const stickyColumns = [
    COLUMNS_IDENTIFIERS.SUPPLIERS, 
    COLUMNS_IDENTIFIERS.PRODUCT_SUBGROUP_NAME, 
    COLUMNS_IDENTIFIERS.PRODUCT_NAME
  ];
  const isMainSticky = stickyColumns.includes(columnId);

  return {
    fontSize: "0.6rem",
    position: "sticky",
    backgroundColor: "#fff", 
    paddingLeft: 1,
    paddingRight: 1,
    paddingTop: 4,
    paddingBottom: 4,
    zIndex: isMainSticky ? "20" : "10"
  };
};

export function SalesRecommendationTable({ actions, ctx, uiState }) {

  const { setShowColumnFilters, setFreezeColumns, setSorting } = actions || {};

  const { purchaseRecommendationData, columns } = ctx || {};

  const { 
    saveLoading,
    inputs,
    sorting,
    showColumnFilters,
    freezeColumns,
   } = uiState || {};

  const [columnVisibility, setColumnVisibility] = useState();

  const [rowHeights, setRowHeights] = useState({});

  const observersRef = useRef({});

  const rowRef = useCallback((depth) => (node) => {
    if (!node || observersRef.current[depth]) return;
    const measure = () => setRowHeights(prev => {
      const newH = node.getBoundingClientRect().height;
      if (prev[depth] === newH) return prev;
      return { ...prev, [depth]: newH };
    });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    observersRef.current[depth] = observer;
  }, []);

  const totalItems = useMemo(() => {
    return purchaseRecommendationData.length;
  }, [purchaseRecommendationData]);

  const table = useReactTable({
    data: purchaseRecommendationData || [],
    columns,
    state: {
      sorting,
      columnVisibility,
    },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', overflowY: 'scroll', maxHeight: '80vh' }}>

      {saveLoading && (
        <UpdatingModal 
          ctx={{ 
            count:Object.keys(inputs).length, 
            label: {
              primaryMessage: t("updatingMessage"),
              dynamicMessagesArr: [
                t("updatingItems"), t("updatingItems_plural")
              ]
            }
          }}
          uiState={{ isOpen: saveLoading }}/>
      )}

      {showColumnFilters && (
        <div style={{ 
          backgroundColor: "#dadddf",
          display: "flex",
          justifyContent: "center",
          margin: "auto",
          padding: "1rem",
          position: "relative",
          top: 0,
          left: 0,
          zIndex: 100,
          width: "30%"
        }}>
          <ColumnVisibilityMemo
            actions={{ setShowColumnFilters: setShowColumnFilters, setFreezeColumns: setFreezeColumns }}
            ctx={{ table: table }}
            uiState={{ freezeColumns: freezeColumns }}/>
        </div>
      )}

      <section style={{ 
        backgroundColor: '#fff',
        border: '2px solid #dee2e6', 
        boxShadow: '0 4px 8px 0 rgba(0,0,0,0.04)',
        boxSizing: 'content-box',
        display: 'flex', 
        flexDirection: 'column', 
        gap: '0.2rem', 
        inlineSize: 'fit-content',
        padding: '0.2rem',
        position: 'sticky',
        zIndex: '1000',
        top: '100%',
        left: "0%" }}>
        <CustomText color={'mediumGrey'} fontSize={['0.3rem', '0.5rem', '0.6rem']} fontWeight={"normal"}>
          {`${t("productsAmount")}: ${table.getFilteredRowModel().rows.length} ${t("of")} ${totalItems}.`}
        </CustomText>  
      
      </section>

      <TableControls salesTable={true}>
        <table
          id="download-table"
          className="table table-hover"
          style={{ width: "100%" }}>
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id} ref={rowRef(headerGroup.depth)}>
                {headerGroup.headers.map(header => {
                  const columnStyle = getColumnStyles({ columnId: header.column.id });
                  const stickyLeft = stickyColumnsStyles({ columnId: header.column.id, table: table });
                  const lateralBordersStyle = bordersAndBackgroundColorStyles({ columnId: header.column.id });
                  const borderBottomStyle = headerGroup.depth === 1 ? '2px solid #DEE2E6' : 'none';
                  const justifyContentStyle = flexColumnsStyles({ columnId: header.column.id });

                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      data-column={header.column.id}
                      style={{
                        textAlign: 'center',
                        cursor: header.column.getCanSort() ? 'pointer' : 'default',
                        backgroundColor: '#f8f9fa',
                        position: 'sticky',
                        left: stickyLeft !== undefined ? `${stickyLeft}px` : undefined,
                        top: `${Object.entries(rowHeights)
                          .filter(([d]) => Number(d) < headerGroup.depth)
                          .reduce((acc, [, h]) => acc + h, 0)}px`,
                        zIndex: 10 - headerGroup.depth,
                        ...columnStyle,
                        ...lateralBordersStyle,
                        borderBottom: borderBottomStyle,
                        ...(header.isPlaceholder ? {
                            borderBottom: 'none',
                            borderLeft: 'none',
                            borderRight: 'none',
                            backgroundColor: '#fff',
                            zIndex: 20, 
                          } : {})                        
                      }}

                      onClick={() => {
                        const current = sorting?.find(s => s.id === header.column.id);
                        let next;

                        if (!current) {
                          next = { id: header.column.id, desc: false };
                        } else {
                          next = { id: header.column.id, desc: !current.desc };
                        }
                        setSorting([next]);
                      }}
                    >
                      {header.isPlaceholder ? null : (
                        <div style={{ display: 'flex', alignItems: 'center', ...justifyContentStyle }}>
                          {flexRender(header.column.columnDef.header, header.getContext())}     
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody>
            {table.getSortedRowModel().rows.length === 0 ? (
              <EmptyTableRow
                ctx={{
                  colSpan: table.getAllColumns().length + 10,
                  labels: {
                    emptyTableMessage: t("choseRegionAndStore")
                  }
                }}/>
            ) : (
            table.getSortedRowModel().rows.map(row => (
              <tr key={row.original.productId}>
                {row.getVisibleCells().map(cell => {
                  const columnStyle = getColumnStyles(cell.column.id);
                  const stickyLeft = stickyColumnsStyles(cell.column.id, table);
                  const stickyColumnsOrder = [
                    COLUMNS_IDENTIFIERS.SUPPLIERS, 
                    COLUMNS_IDENTIFIERS.PRODUCT_SUBGROUP_NAME, 
                    COLUMNS_IDENTIFIERS.PRODUCT_NAME
                  ];
                  const lateralBordersStyle = bordersAndBackgroundColorStyles(cell.column.id);            

                  return (
                    <td
                      key={cell.id}
                      data-column={cell.column.id}
                      style={{
                        textAlign: 'left',
                        verticalAlign: 'middle',
                        whiteSpace: "nowrap",
                        left: (stickyLeft !== undefined && stickyColumnsOrder.includes(cell.column.id)) ? `${stickyLeft}px` : undefined,
                        borderBottom: '1px solid #dee2e6',
                        ...columnStyle,
                        ...lateralBordersStyle,
                        fontSize: "0.75rem",
                        zIndex: columnStyle.zIndex ? columnStyle.zIndex - 1 : undefined,              
                      }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  );
                })}
              </tr>
            ))
            )}
          </tbody>

        </table>
      </TableControls>
    </div>
  );
};

SalesRecommendationTable.propTypes = {
  actions: PropTypes.shape(),
  ctx: PropTypes.shape(),
  uiState: PropTypes.shape()
};

ColumnVisibilityMemo.propTypes = {
  actions: PropTypes.shape(),
  ctx: PropTypes.shape(),
  uiState: PropTypes.shape()
};

EmptyTableRow.propTypes = { ctx: PropTypes.shape() };